import React, { useState } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Plus, Pencil, Trash2, Users, MapPin, Clock, CheckCircle2, Loader2 } from "lucide-react";
import { dataItalianaLunga } from "@/components/accessi/utils";

export const EventiAccessi = ({ eventi, gruppi, annoCorrente, current, onSelect, reload }) => {
  const { can } = useAuth();
  const manage = can("accessi.manage");
  const empty = { nome_evento: "", data_evento: new Date().toISOString().slice(0, 10), gruppo: "", luogo: "", ora_inizio: "", anno_riferimento: annoCorrente, note: "" };
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEdit(null); setF({ ...empty, anno_riferimento: annoCorrente }); setOpen(true); };
  const openEdit = (ev) => { setEdit(ev); setF({ ...empty, ...ev }); setOpen(true); };

  const save = async () => {
    if (!f.nome_evento.trim()) { toast.error("Inserisci il nome dell'evento"); return; }
    if (!f.data_evento) { toast.error("Inserisci la data dell'evento"); return; }
    setSaving(true);
    const payload = {
      nome_evento: f.nome_evento.trim(), data_evento: f.data_evento,
      gruppo: f.gruppo || null, luogo: f.luogo || null, ora_inizio: f.ora_inizio || null,
      anno_riferimento: Number(f.anno_riferimento) || annoCorrente, note: f.note || null,
    };
    try {
      if (edit) await api.put(`/accessi/eventi/${edit.id}`, payload);
      else await api.post("/accessi/eventi", payload);
      toast.success("Evento salvato"); setOpen(false); reload();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Eliminare l'evento "${ev.nome_evento}"?`)) return;
    try { await api.delete(`/accessi/eventi/${ev.id}`); toast.success("Evento eliminato"); reload(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <p className="text-sm text-muted-foreground">Seleziona l'evento su cui registrare gli accessi, oppure creane uno nuovo.</p>
        <Button onClick={openNew} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="accessi-nuovo-evento">
          <Plus className="h-4 w-4 mr-2" /> Nuovo evento
        </Button>
      </div>
      {(!eventi || eventi.length === 0) ? (
        <EmptyState title="Nessun evento" description="Crea il primo evento/sessione per iniziare." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {eventi.map((ev) => {
            const active = current?.id === ev.id;
            return (
              <Card key={ev.id} className={`p-4 min-w-0 ${active ? "border-[color:var(--brand-green-600)] ring-1 ring-[color:var(--brand-green-600)]" : ""}`} data-testid={`evento-card-${ev.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{ev.nome_evento}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {dataItalianaLunga(ev.data_evento)}</span>
                      {ev.gruppo && <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ev.gruppo}</span>}
                      {ev.luogo && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ev.luogo}</span>}
                      {ev.ora_inizio && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {ev.ora_inizio}</span>}
                    </div>
                  </div>
                  {active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)] shrink-0">In uso</span>}
                </div>
                <div className="flex items-center gap-3 mt-3 text-sm">
                  <span className="text-[color:var(--brand-green-700)] font-medium">{ev.conteggi?.presenze ?? 0} presenze</span>
                  <span className="text-[color:var(--brand-orange-600)] font-medium">{ev.conteggi?.sospesi ?? 0} in sospeso</span>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <Button size="sm" onClick={() => onSelect(ev)} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid={`evento-seleziona-${ev.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> {active ? "Registra" : "Seleziona"}
                  </Button>
                  {manage && <Button size="sm" variant="outline" onClick={() => openEdit(ev)} data-testid={`evento-edit-${ev.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {manage && <Button size="sm" variant="outline" onClick={() => remove(ev)} data-testid={`evento-del-${ev.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="evento-dialog">
          <DialogHeader><DialogTitle>{edit ? "Modifica evento" : "Nuovo evento"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Nome evento *</Label><Input value={f.nome_evento} onChange={(e) => setF({ ...f, nome_evento: e.target.value })} data-testid="evento-nome" /></div>
            <div><Label>Data *</Label><Input type="date" value={f.data_evento} onChange={(e) => setF({ ...f, data_evento: e.target.value })} data-testid="evento-data" /></div>
            <div><Label>Ora inizio</Label><Input type="time" value={f.ora_inizio || ""} onChange={(e) => setF({ ...f, ora_inizio: e.target.value })} data-testid="evento-ora" /></div>
            <div>
              <Label>Scuola / Gruppo</Label>
              <Select value={f.gruppo || undefined} onValueChange={(v) => setF({ ...f, gruppo: v })}>
                <SelectTrigger data-testid="evento-gruppo"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{gruppi.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Anno di riferimento</Label><Input type="number" value={f.anno_riferimento || ""} onChange={(e) => setF({ ...f, anno_riferimento: e.target.value })} data-testid="evento-anno" /></div>
            <div className="sm:col-span-2"><Label>Luogo</Label><Input value={f.luogo || ""} onChange={(e) => setF({ ...f, luogo: e.target.value })} data-testid="evento-luogo" /></div>
            <div className="sm:col-span-2"><Label>Note</Label><Textarea value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="evento-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="evento-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
